import {
  type DynamicClient,
  InvalidParamError,
} from '@dynamic-labs-sdk/client';
import type {
  ExecuteCheckoutTransactionParams,
  ExecuteCheckoutTransactionResult,
} from '@dynamic-labs-sdk/client/core';
import { getWalletProviderFromWalletAccount } from '@dynamic-labs-sdk/client/core';

import type { TronSigningPayload } from '../../checkoutTransaction.types';
import { isTronWalletAccount } from '../../isTronWalletAccount';
import { isTronWalletProvider } from '../../isTronWalletProvider';

/**
 * Executes a Tron checkout transaction by deserializing the transaction from
 * the signing payload, signing it with TronWeb, and broadcasting it.
 *
 * 1. Validates that the wallet account is a Tron wallet account
 * 2. Extracts the serialized transaction from the signing payload
 * 3. Parses, signs, and broadcasts via TronWeb
 *
 * @param params.signingPayload - The chain-aware signing payload containing `serializedTransaction`.
 * @param params.walletAccount - The wallet account used to sign and send the transaction.
 * @param params.onStepChange - Optional callback invoked with `'transaction'` when the signing step begins.
 * @param client - The Dynamic client instance.
 * @returns The transaction hash from the broadcast result.
 */
export const tronExecuteCheckoutTransaction = async (
  {
    onStepChange,
    signingPayload,
    walletAccount,
  }: ExecuteCheckoutTransactionParams,
  client: DynamicClient
): Promise<ExecuteCheckoutTransactionResult> => {
  const provider = getWalletProviderFromWalletAccount(
    { walletAccount },
    client
  );

  if (!isTronWalletAccount(walletAccount) || !isTronWalletProvider(provider)) {
    throw new InvalidParamError('Wallet account is not a Tron wallet account');
  }

  const tronPayload = signingPayload as TronSigningPayload;

  if (!tronPayload.serializedTransaction?.serializedTransaction) {
    throw new InvalidParamError(
      'Signing payload is missing serialized transaction data'
    );
  }

  onStepChange?.('transaction');

  const tronWeb = await provider.getTronWeb({ walletAccount });

  const unsignedTransaction = JSON.parse(
    tronPayload.serializedTransaction.serializedTransaction
  );

  const signedTransaction = await tronWeb.trx.sign(unsignedTransaction);
  const broadcastResult = await tronWeb.trx.sendRawTransaction(
    signedTransaction
  );

  return { transactionHash: broadcastResult.txid };
};
